import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Save } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { seoGetSettings, seoSaveSettings, SEO_STR as S } from './seoApi'

/**
 * Umumiy SEO config-ekran formasi. A12.17–A12.21 (Core Web Vitals / PageSpeed /
 * mobil moslik / Search Console / Yandex Webmaster) — bir xil "seo_* sozlamalarni
 * yuklash + tahrirlash + saqlash" naqshi. Backend `GET/PUT /admin/seo/settings`:
 * maxfiy kalitlar (api_key/token) qaytarilmaydi — faqat "sozlangan" belgisi keladi;
 * bo'sh maxfiy maydon SAQLANMAYDI (mavjud kalit o'chib ketmaydi). Real config —
 * soxta ma'lumot yo'q. Placeholder huquq SeoConfigTable bilan bir xil (USERS_VIEW).
 */
export type SeoSettingKind = 'text' | 'number' | 'secret' | 'select' | 'bool'

export interface SeoSettingField {
  /** app_settings kaliti — `seo_` bilan boshlanishi SHART. */
  key: string
  label: string
  kind: SeoSettingKind
  placeholder?: string
  mono?: boolean
  /** `select` uchun. */
  options?: { value: string; label: string }[]
  help?: string
}

export interface SeoSettingsFormProps {
  screenId: string
  title: string
  fields: SeoSettingField[]
  icon?: ReactNode
  /** Forma tepasidagi ixtiyoriy izoh (masalan "qiymatlar qo'lda kiritiladi"). */
  hint?: string
}

export function SeoSettingsForm({ screenId, title, fields, hint }: SeoSettingsFormProps) {
  const { toast } = useToast()
  const [values, setValues] = useState<Record<string, string>>({})
  const [secrets, setSecrets] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    setLoading(true)
    seoGetSettings(ac.signal)
      .then((d) => {
        setValues(d.settings)
        setSecrets(d.secrets)
      })
      .finally(() => setLoading(false))
    return () => ac.abort()
  }, [])

  const set = (k: string, v: string) => setValues((s) => ({ ...s, [k]: v }))

  const save = async () => {
    setBusy(true)
    try {
      const body: Record<string, string> = {}
      // Faqat shu ekran maydonlarini yuboramiz (boshqa seo_* kalitlarga tegmaymiz).
      for (const f of fields) body[f.key] = (values[f.key] ?? '').trim()
      const res = await seoSaveSettings(body)
      // Maxfiy maydon bo'sh qoldirilsa — o'zgarmaydi; bo'sh emas → endi sozlangan.
      setSecrets((prev) => {
        const next = { ...prev }
        for (const f of fields) {
          if (f.kind === 'secret' && (values[f.key] ?? '').trim()) next[f.key] = true
        }
        return next
      })
      for (const f of fields) {
        if (f.kind === 'secret') set(f.key, '')
      }
      toast(res?.ok ? S.settingsSaved : S.saveFailed, { tone: res?.ok ? 'success' : 'error' })
    } catch {
      toast(S.saveFailed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const inputCls =
    'w-full rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-primary outline-none focus:border-border-strong'

  return (
    <>
      <PageHeader
        title={title}
        screenId={screenId}
        actions={
          <button
            type="button"
            onClick={save}
            disabled={busy || loading}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            <Save size={14} />
            {S.save}
          </button>
        }
      />
      <div className="min-h-0 flex-1 space-y-3 overflow-auto p-4">
        {hint ? (
          <p className="max-w-2xl text-sm text-tertiary">{hint}</p>
        ) : null}
        <div className="grid max-w-2xl gap-3">
          {fields.map((f) => (
            <label key={f.key} className="flex flex-col gap-1">
              <span className="text-sm font-medium text-secondary">{f.label}</span>
              {f.kind === 'select' ? (
                <select
                  value={values[f.key] ?? ''}
                  onChange={(e) => set(f.key, e.target.value)}
                  disabled={loading}
                  className={inputCls}
                >
                  <option value="">—</option>
                  {(f.options ?? []).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : f.kind === 'bool' ? (
                <select
                  value={values[f.key] ?? ''}
                  onChange={(e) => set(f.key, e.target.value)}
                  disabled={loading}
                  className={inputCls}
                >
                  <option value="">—</option>
                  <option value="true">{S.yes}</option>
                  <option value="false">{S.no}</option>
                </select>
              ) : (
                <input
                  type={f.kind === 'number' ? 'number' : f.kind === 'secret' ? 'password' : 'text'}
                  value={values[f.key] ?? ''}
                  onChange={(e) => set(f.key, e.target.value)}
                  disabled={loading}
                  spellCheck={false}
                  autoComplete={f.kind === 'secret' ? 'new-password' : 'off'}
                  placeholder={
                    f.kind === 'secret' && secrets[f.key] ? S.secretPlaceholder : f.placeholder ?? ''
                  }
                  className={`${inputCls} ${f.mono ? 'font-mono' : ''}`}
                />
              )}
              {f.kind === 'secret' && secrets[f.key] ? (
                <span className="text-xs text-tertiary">
                  {S.secretSet} — {S.secretHint}
                </span>
              ) : f.help ? (
                <span className="text-xs text-tertiary">{f.help}</span>
              ) : null}
            </label>
          ))}
        </div>
      </div>
    </>
  )
}
